import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { AlertTriangle, CheckCircle, XCircle, ListChecks } from 'lucide-react';
import AssessmentResetDialog from './AssessmentResetDialog';

const AttemptsBadge = ({ course }) => {
  if (course.maxed_out_assessments > 0) {
    return (
      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
        <XCircle className="h-3 w-3 mr-1" />
        {course.maxed_out_assessments} assessment(s) at max attempts
      </Badge>
    );
  }
  if (course.status === 'completed') {
    return (
      <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">
        <CheckCircle className="h-3 w-3 mr-1" />
        Completed
      </Badge>
    );
  }
  if (course.total_quiz_attempts > 0) {
    return (
      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
        <AlertTriangle className="h-3 w-3 mr-1" />
        {course.total_quiz_attempts} quiz attempt(s)
      </Badge>
    );
  }
  return <Badge variant="outline">No quiz attempts</Badge>;
};

const rowTone = (course) => {
  if (course.maxed_out_assessments > 0) return 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30';
  if (course.status === 'completed') return 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30';
  return 'border-border';
};

const CourseProgressResetList = ({ api, progressData, moduleType, resetKey, user, icon, onReset }) => {
  const [openCourse, setOpenCourse] = useState(null);
  if (!progressData) return null;

  return (
    <>
      <ScrollArea className="h-[300px] pr-4">
        <div className="space-y-3">
          {!progressData.progress?.length ? (
            <p className="text-center text-muted-foreground py-8" data-testid={`course-reset-empty-${moduleType}`}>
              No course progress found for this user
            </p>
          ) : (
            progressData.progress.map((course) => (
              <div
                key={course.course_id}
                className={`p-3 rounded-lg border ${rowTone(course)}`}
                data-testid={`course-reset-row-${moduleType}-${course.course_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {icon}
                      <p className="font-medium truncate">{course.course_name || course.course_id}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
                      <AttemptsBadge course={course} />
                      {course.overall_score > 0 && (
                        <span className="text-muted-foreground">Score: {Math.round(course.overall_score)}%</span>
                      )}
                      {course.quiz_count > 0 && (
                        <span className="text-muted-foreground">{course.quiz_count} quiz(es) attempted</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Progress: {Math.round(course.overall_progress_percent || 0)}%
                    </div>
                  </div>
                  {course.total_quiz_attempts > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-amber-600 border-amber-200 hover:bg-amber-50 shrink-0"
                      onClick={() => setOpenCourse(course)}
                      data-testid={`reset-retry-${moduleType}-${course.course_id}`}
                    >
                      <ListChecks className="h-4 w-4 mr-1" />
                      Choose assessments
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
      {openCourse && (
        <AssessmentResetDialog
          api={api}
          resetKey={resetKey}
          user={user}
          course={openCourse}
          onClose={() => setOpenCourse(null)}
          onReset={onReset}
        />
      )}
    </>
  );
};

export default CourseProgressResetList;
